import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { formatCr } from "@/lib/format";
import { Radar, ChevronRight } from "lucide-react";
import { SourceBadge } from "./SourceBadge";

export const STAGE_META = {
  lead: { label: "Lead", color: "#94a3b8" },
  submitted: { label: "Submitted", color: "#0284C7" },
  verification: { label: "Verification", color: "#0e7490" },
  info_required: { label: "Info required", color: "#d97706" },
  review: { label: "Credit / Risk", color: "#6366f1" },
  committee: { label: "Committee", color: "#7c3aed" },
  sanctioned: { label: "Sanctioned", color: "#0d9488" },
  approved: { label: "Docs / CP", color: "#059669" },
  documentation_complete: { label: "Docs complete", color: "#047857" },
  disbursed: { label: "Disbursed", color: "#b3122a" },
  declined: { label: "Declined", color: "#dc2626" },
};

function Kpi({ label, value, sub, onClick, testid }) {
  return (
    <div onClick={onClick} data-testid={testid}
      className={`ceo-card p-4 ${onClick ? "cursor-pointer ceo-card--click" : ""}`}>
      <div className="ceo-label">{label}</div>
      <div className="ceo-fig text-[26px] leading-tight text-[#111111] mt-1.5 tabular-nums">{value}</div>
      {sub && <div className="text-[11px] text-slate-400 mt-1">{sub}</div>}
    </div>
  );
}

export default function CockpitLive({ refreshKey = 0 }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const controller = new AbortController();
    api.get("/v3/cockpit", { signal: controller.signal }).then((r) => { setData(r.data); setErr(null); }).catch((e) => { if (e.code !== "ERR_CANCELED") setErr(e.message); });
    return () => controller.abort();
  }, [refreshKey]);

  if (err) return <div role="alert" data-testid="cockpit-live-error" className="text-sm text-red-700 mb-6">Live origination could not be refreshed. Use Refresh above to retry.</div>;
  if (!data)
    return <div className="mb-8 text-[12px] text-slate-400" data-testid="cockpit-live-loading">Loading live origination…</div>;

  const { kpis, funnel, action_queue, top_sources, v3_activity, colending } = data;
  const maxF = Math.max(...funnel.map((f) => f.count), 1);
  const go = (stage, aging) => navigate(`/pipeline3${stage ? `?stage=${stage}` : aging ? `?aging=${aging}` : ""}`);

  return (
    <section className="mb-10" data-testid="cockpit-live">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Radar size={14} className="text-[#b3122a]" />
        <span className="ceo-label !text-[#b3122a]">Origination — live LOS</span>
        <SourceBadge source="HISTORICAL" />
        <span data-testid="cockpit-live-latest-date" className="text-[11px] text-slate-400">current state · last application {kpis.last_application_at || "—"}</span>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-8 gap-4 mb-5" data-testid="live-kpi-strip">
        <Kpi testid="live-kpi-apps-mtd" label="Applications MTD" value={kpis.applications_mtd} sub={`${kpis.applications_ytd} YTD`} onClick={() => go("")} />
        <Kpi testid="live-kpi-under-review" label="Under review" value={kpis.under_review} onClick={() => go("submitted")} />
        <Kpi testid="live-kpi-info" label="Info required" value={kpis.info_required} onClick={() => go("info_required")} />
        <Kpi testid="live-kpi-committee" label="Committee pending" value={kpis.committee_pending} onClick={() => go("committee")} />
        <Kpi testid="live-kpi-sanctioned" label="Sanctioned value" value={kpis.sanctioned_value ? formatCr(kpis.sanctioned_value) : "—"} sub={`${kpis.sanctioned_count} files (all time)`} onClick={() => go("sanctioned")} />
        <Kpi testid="live-kpi-receivables" label="Receivables (overdue)"
          value={kpis.receivables_overdue != null ? formatCr(kpis.receivables_overdue) : "Not available"}
          sub={kpis.receivables_as_of ? `snapshot ${kpis.receivables_as_of}` : "no snapshot data"}
          onClick={() => navigate("/portfolio")} />
        <Kpi testid="live-kpi-approved" label="Docs / CP" value={kpis.approved_total} onClick={() => go("approved")} />
        <Kpi testid="live-kpi-disbursed" label="Disbursed (all time)" value={kpis.disbursed_total} onClick={() => go("disbursed")} />
      </div>

      <div className="grid lg:grid-cols-2 xl:grid-cols-4 gap-4">
        <div className="ceo-card p-5 lg:col-span-1" data-testid="cockpit-funnel">
          <div className="ceo-label mb-3">Pipeline distribution (current)</div>
          <div className="space-y-2">
            {funnel.map((f) => (
              <button key={f.stage} onClick={() => go(f.stage)} data-testid={`funnel-bar-${f.stage}`}
                className="w-full flex items-center gap-2 group">
                <span className="w-24 text-left text-[11px] font-semibold text-slate-500 shrink-0">{STAGE_META[f.stage].label}</span>
                <span className="flex-1 h-4 bg-slate-100 rounded-sm overflow-hidden">
                  <span className="block h-full rounded-sm group-hover:opacity-80 transition-opacity"
                    style={{ width: `${Math.max((f.count / maxF) * 100, f.count ? 3 : 0)}%`, background: STAGE_META[f.stage].color }} />
                </span>
                <span className="w-10 text-right text-[12px] font-bold tabular-nums text-slate-700">{f.count}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="ceo-card p-5" data-testid="ceo-action-queue">
          <div className="ceo-label mb-3">CEO action queue</div>
          <div className="space-y-1">
            {action_queue.map((a) => (
              <button key={a.label} onClick={() => go(a.stage, a.aging)} data-testid="action-queue-row"
                className="w-full flex items-center justify-between gap-2 px-2.5 py-2 rounded hover:bg-slate-50 text-left">
                <span className="text-[13px] text-slate-700">{a.label}</span>
                <span className="flex items-center gap-1.5">
                  <span className={`text-[13px] font-bold tabular-nums ${a.count > 0 ? "text-[#b3122a]" : "text-slate-400"}`}>{a.count}</span>
                  <ChevronRight size={13} className="text-slate-300" />
                </span>
              </button>
            ))}
          </div>
        </div>

        <div className="ceo-card p-5" data-testid="top-sources-card">
          <div className="ceo-label mb-3">Top sourcing users (12 mo)</div>
          {top_sources.length === 0 ? (
            <div className="text-[12px] text-slate-400">Not available</div>
          ) : (
            <div className="space-y-1.5">
              {top_sources.map((t, i) => (
                <div key={i} className="flex items-center justify-between gap-2 px-1" data-testid="top-source-row">
                  <span className="text-[13px] text-slate-700 truncate">{t.name}
                    <span className={`ml-1.5 text-[10px] font-bold uppercase ${t.role === "DSA" ? "text-[#b3122a]" : "text-slate-400"}`}>{t.role}</span>
                  </span>
                  <span className="text-[13px] font-bold tabular-nums text-slate-800">{t.count}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex flex-col gap-4">
          <div className="ceo-card p-5 flex-1" data-testid="v3-activity-card">
            <div className="flex items-center gap-2 mb-2">
              <span className="ceo-label">HQ activity</span>
              <SourceBadge source="TEST_V3" />
            </div>
            <button onClick={() => navigate("/pipeline3?source=v3")} data-testid="v3-activity-apps" className="text-left w-full hover:bg-slate-50 rounded px-1 py-0.5">
              <div className="ceo-fig text-[24px] tabular-nums text-[#111111]">{v3_activity?.applications ?? 0}</div>
              <div className="text-[11px] text-slate-400">HQ applications · {v3_activity?.applications_value ? formatCr(v3_activity.applications_value) : "₹0"} asked · {v3_activity?.audit_events ?? 0} audit events</div>
            </button>
          </div>
          <div className="ceo-card p-5 flex-1" data-testid="colending-card">
            <div className="ceo-label mb-1.5">Co-lending line utilization</div>
            <div className="text-[13px] font-bold text-slate-500">Not available</div>
            <div className="text-[11px] text-slate-400 mt-0.5">{colending?.note || "No co-lending data exists in the historical system."}</div>
          </div>
        </div>
      </div>
    </section>
  );
}
